'use client';
import { useMediaQuery } from 'react-responsive';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import YoutubeVideo from '@repo/ui/src/components/YoutubeVideo';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import buildLinkObj, { getRel } from '@repo/ui/src/functions/build-link';
import YoutubePlayIcon from '@repo/ui/src/components/Icons/YoutubePlayIcon';
import { AssetProps, LinkProps, MediaType, YoutubeLinkProps } from '@repo/ui/src/types';

interface Props {
  imgOrVideo: 'Image' | 'Video';
  image: MediaType;
  youtubeLink: YoutubeLinkProps;
  youtubeLinkThumbnail: AssetProps;
  order: number;
  _testID: string;
}

const FullWidthAsset = (props: Props) => {
  const { push } = useModal(ModalID.video);
  const { imgOrVideo, image, youtubeLink, youtubeLinkThumbnail, order, _testID } = props;
  const imgUrl = buildImageSrc(image, 1800, 3, 1);
  const youtubeImgUrl = buildImageSrc(youtubeLinkThumbnail, 1800, 3, 1);
  const blackOverlayUrl =
    'https://i8.amplience.net/i/traeger/black-image/?w=1800&h=650&sm=c&scaleFit=poi&$poi2$';

  const isMediumScreenOrSmaller = useMediaQuery({
    query: '(max-width: 993px)',
  });

  return (
    <div
      className={`jumbotron p-0 my-0 ${imgOrVideo === 'Video' ? 'rotating-background' : ''}`}
      style={{ zIndex: order }}
      data-test-id={`full_width_asset_${_testID}`}
    >
      {imgOrVideo === 'Image' || image ? (
        <LazyLoadImage
          imgComponent={
            <img
              className="card-img-top img-fluid w-full block"
              src={imgUrl}
              alt={image.name ? image.name : 'image not found'}
            />
          }
        />
      ) : youtubeLink && youtubeLinkThumbnail ? (
        <div>
          <div onClick={() => push({ youtubeLink })}>
            <div className="relative">
              <LazyLoadImage
                imgComponent={
                  <img
                    className="cursor-pointer card-img-top block m-auto w-full rounded-none yotube-img-container absolute left-0 right-0 opacity-60"
                    src={blackOverlayUrl}
                    alt="youtube thumbnail"
                  />
                }
              />
              <div className="absolute top-1/2 left-1/2 translate-y-1/2 translate-x-1/2">
                <YoutubePlayIcon
                  color="#fff"
                  height={isMediumScreenOrSmaller ? '100px' : '250px'}
                />
              </div>

              <LazyLoadImage
                imgComponent={
                  <img
                    className="cursor-pointer card-img-top block m-auto w-full rounded-none z-[-1]"
                    src={youtubeImgUrl}
                    alt="youtube play icon"
                  />
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <YoutubeVideo {...youtubeLink} />
      )}
    </div>
  );
};

interface WrapperProps extends Props {
  imageLink?: LinkProps[];
}

const FullWidthAssetWrapper = ({ imageLink, ...otherProps }: WrapperProps) => {
  const linkObj =
    imageLink && Array.isArray(imageLink) && imageLink.length === 1
      ? buildLinkObj(imageLink[0])
      : null;
  return linkObj ? (
    <a rel={getRel(linkObj)} href={linkObj.href}>
      <FullWidthAsset {...otherProps} />
    </a>
  ) : (
    <FullWidthAsset {...otherProps} />
  );
};

export default FullWidthAssetWrapper;
